import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';

import {AuthFacade} from 'src/app/store/auth-state/+state/auth.facade';
import {
	combineLatest,
	filter,
	map,
	of,
	Subject,
	switchMap,
	take,
	takeUntil
} from 'rxjs';

import {ManagerFacade} from 'src/app/store/manager/+state/manager.facade';
import {OrderStatus} from 'src/app/models/OrderStatus';
import {OrderType} from 'src/app/models/OrderType';
import * as moment from 'moment';
import {Socket} from 'ngx-socket-io';
import {SocketRole} from 'src/app/models/SocketRoles';
import Order from 'src/app/models/Order';
import {OrderService, STATUS_CHANGE} from 'src/app/services/order.service';
import {Table} from 'src/app/models/Table';
import {NavigationService} from 'src/app/services/navigation.service';
import {ArticleModalComponent} from 'src/app/components/article-modal/article-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {OrderPool} from 'src/app/models/OrderPool';
import {OrderChannelsFilter} from 'src/app/models/OrderChannels';
import Article from 'src/app/models/Article';
import {PrinterService} from 'src/app/services/printer.service';
import {OptionGroup} from 'src/app/models/OptionGroup';
import {AppComponent} from '../../app.component';
import {Venue} from 'src/app/models/Venue';
import {
	checkIfManualAcceptedView,
	filterBoardItems,
	filterOrderChannel,
	groupOptionsByGroup,
	STATUS_ORDER
} from 'src/app/components/manager-components/manager-utils/manager-utils';

@Component({
	selector: 'app-manger',
	templateUrl: './manger.page.html',
	styleUrls: ['./manger.page.scss']
})
export class ManagerPage implements OnDestroy, OnInit {
	private static readonly SOUND_PLAYED_STORAGE_KEY = 'received_orders';
	private static readonly SOUND_PLAYED_LIMIT = 100;

	@ViewChild('scrollableContent', {static: false})
	scrollableContent!: ElementRef;
	private readonly onDestroy$ = new Subject<void>();
	private readonly stopListeningSocketForTable$ = new Subject<void>();
	public readonly updateStatusLoading$ =
		this.managerFacade.updateStatusLoading$;
	public venue$ = this.managerFacade.venue$;
	private currentVenue: Venue | null = null;
	public allCategories$ = this.managerFacade.categories$;
	public orderChannels: OrderChannelsFilter = [];

	public mappedArticleWithOptionGroups$ = combineLatest(
		this.managerFacade.categories$,
		this.managerFacade.optionGroups$
	).pipe(
		switchMap(([categories, optionGroups]) => {
			const allArticles: Article[] = [];
			categories.forEach(it => {
				it.articles?.forEach(article => {
					allArticles.push(article);
				});
			});
			const collectionOfGroups = new Map(optionGroups.map(it => [it._id, it]));
			let articles = JSON.parse(JSON.stringify(allArticles)) as Article[];
			articles = articles.map(it => {
				it.groups = it.groups.map((group: any) => {
					if (collectionOfGroups.has(group)) {
						group = collectionOfGroups.get(group);
					}
					return group;
				});
				return it;
			});
			return of(articles);
		})
	);
	private filteredOrders$ = combineLatest([
		this.managerFacade.orders$,
		this.managerFacade.ordersPool$,
		this.managerFacade.orderStatusFiler$,
		this.managerFacade.orderChannelsFilter$
	]).pipe(
		map(([ordersValue, poolValue, status, orderChannels]) => {
			this.orderChannels = orderChannels;
			const {orders, pools} = filterBoardItems(
				ordersValue,
				poolValue,
				status,
				orderChannels
			);
			// Only the visible items are copied, they are mapped in place below
			return structuredClone([...pools, ...orders])
				.sort(this.sortByTime)
				.sort(this.sortByStatus);
		})
	);
	public orders$ = combineLatest([
		this.filteredOrders$,
		this.managerFacade.optionGroups$
	]).pipe(
		map(([items, optionGroups]) => {
			const groupsMap = new Map(optionGroups.map(it => [it._id, it]));
			return items.map(item =>
				this.checkIfOrder(item)
					? this.mapOrderOptionGroups(item, groupsMap)
					: item
			);
		})
	);
	public statusChangeENUM = STATUS_CHANGE;
	constructor(
		private authFacade: AuthFacade,
		private managerFacade: ManagerFacade,
		private socket: Socket,
		private orderService: OrderService,
		private navService: NavigationService,
		public dialog: MatDialog,
		private printer: PrinterService
	) {}

	async ngOnInit() {
		this.authFacade.userData$
			.pipe(
				filter(userValue => !!userValue),
				map(userData => {
					return userData?.venues[0] ?? null;
				}),
				takeUntil(this.onDestroy$)
			)
			.subscribe(venueId => {
				if (venueId) {
					this.loadDataByUser(venueId);
				}
			});

		this.venue$
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(venue => (this.currentVenue = venue));

		this.managerFacade.tables$
			.pipe(
				filter(device => device.length > 0),
				takeUntil(this.stopListeningSocketForTable$ || this.onDestroy$)
			)
			.subscribe(tables => {
				console.log('table', tables);
				if (tables.length > 0) {
					this.listenSocket(tables);
				}
			});

		this.orders$.subscribe(v => console.log(v));
	}

	private loadDataByUser(venueId: string) {
		this.managerFacade.loadVenueData({venueId});
		this.loadOrdersWithDefaultConfig(venueId);
	}

	ngOnDestroy() {
		this.onDestroy$.next();
	}

	private loadOrdersWithDefaultConfig(venueId: string) {
		this.managerFacade.loadOrders({
			venue: venueId,
			status: [
				OrderStatus.AWAITING_CONFIRMATION,
				OrderStatus.IN_PREPARATION,
				OrderStatus.READY,
				OrderStatus.DONE
			],
			orderTypes: [
				OrderType.TERMINAL,
				OrderType.STANDARD,
				OrderType.VIRTUAL,
				OrderType.PREORDER
			],
			from: moment().subtract(1, 'days').startOf('day').toString(),
			to: moment().add(2, 'days').toString(),
			promoCode: true
		});
		this.managerFacade.loadOrdersPool({
			venue: venueId,
			status: [OrderStatus.IN_PREPARATION, OrderStatus.READY, OrderStatus.DONE],
			orderTypes: [
				OrderType.TERMINAL,
				OrderType.STANDARD,
				OrderType.VIRTUAL,
				OrderType.PREORDER
			],
			from: moment().startOf('day').toString(),
			to: moment().add(2, 'days').toString(),
			promoCode: true
		});
		this.listenSocketOrders(venueId);
	}

	private listenSocketOrders(venueId: string) {
		console.log('here');
		this.socket.on('connect', () => {
			this.authenticateSocket({
				roles: [SocketRole.COUNTER_DISPLAY, SocketRole.COUNTER_DISPLAY_MAIN],
				venueIds: [venueId]
			});
		});

		this.socket.on('venue-' + venueId + '-orders', (data: any) => {
			if (data?.order && (data?.type == 'update' || data?.type == 'created')) {
				console.log('LISTEN', data);

				if (this.shouldPlaySoundForOrder(data.order)) {
					this.markSoundPlayed(data.order._id);
					this.playClickSound();
				}
				if (data.type == 'update' && this.printer.isConnected) {
					this.printOrder(data.order);
				}
				this.updateState(data.order);
			}
		});
		// this.socket.on('venue-' + venueId + '-order-pools', (data: any) => {
		// 	console.log('LISTEN pools', data);
		//
		// 	if (data?.orderPool && data?.type == 'updated') {
		// 		console.log('LISTEN pools', data);
		// 		this.playClickSound();
		// 		this.managerFacade.addOrderPoolNew(data?.orderPool);
		// 	}
		// });
	}

	public printOrder(order: any) {
		let allOrders: string[] = [];
		try {
			allOrders = JSON.parse(localStorage.getItem('printerOrders') ?? '') ?? [];
		} catch (e) {
			allOrders = [];
		}

		if (order) {
			if (!allOrders.includes(order?._id)) {
				of(order)
					.pipe(
						switchMap(order => {
							return this.mappedOrder(order).pipe(take(1));
						}),
						switchMap((order: Order) => {
							order.orderedArticles = order.orderedArticles.map(it => {
								return {
									...it,
									groupsForRender: groupOptionsByGroup(it.groups)
								};
							});
							return of(order);
						})
					)
					.subscribe(v => {
						console.log('check My Function', v);
						allOrders.push(v._id);
						if (allOrders.length < 30) {
							localStorage.setItem('printerOrders', JSON.stringify(allOrders));
						} else {
							localStorage.setItem('printerOrders', JSON.stringify([]));
						}
					});
			}
		}
	}

	private updateState(order: Order) {
		this.managerFacade.updateOrder(order);
	}

	private shouldPlaySoundForOrder(order: Order): boolean {
		const isAwaitingConfirmation =
			order?.status === OrderStatus.AWAITING_CONFIRMATION;

		const isAcceptedIntoPreparation =
			order?.status === OrderStatus.IN_PREPARATION;

		const needsManualAcceptance = checkIfManualAcceptedView(
			order,
			this.currentVenue
		);

		const isRelevantOrderEvent =
			isAwaitingConfirmation ||
			isAcceptedIntoPreparation ||
			needsManualAcceptance;

		const isChannelVisible =
			this.filterOrderChannel([order], this.orderChannels).length > 0;

		return (
			isRelevantOrderEvent &&
			isChannelVisible &&
			!this.wasSoundPlayed(order._id)
		);
	}

	private getSoundPlayedOrderIds(): string[] {
		try {
			return JSON.parse(
				localStorage.getItem(ManagerPage.SOUND_PLAYED_STORAGE_KEY) || '[]'
			);
		} catch (e) {
			return [];
		}
	}

	private wasSoundPlayed(orderId: string | undefined): boolean {
		return !!orderId && this.getSoundPlayedOrderIds().includes(orderId);
	}

	// Newest ids go first; the oldest ones are cut off once the limit is reached
	private markSoundPlayed(orderId: string | undefined) {
		if (!orderId) return;
		const orderIds = this.getSoundPlayedOrderIds();
		orderIds.unshift(orderId);
		if (orderIds.length > ManagerPage.SOUND_PLAYED_LIMIT) {
			orderIds.length = ManagerPage.SOUND_PLAYED_LIMIT;
		}
		localStorage.setItem(
			ManagerPage.SOUND_PLAYED_STORAGE_KEY,
			JSON.stringify(orderIds)
		);
	}

	private authenticateSocket(data: {
		roles: SocketRole[];
		venueIds: string[];
	}): void {
		this.socket.emit('authenticate', data.roles, data.venueIds, (ack: any) => {
			console.log('Socket authenticated for ', ack, data);
		});
	}

	private listenSocket(tables: Table[]) {
		console.log('load Socket');
		// tables.forEach(it => {
		// 	this.socket.on(
		// 		`terminalStatus_${it.venue}_${it.number.replace(' ', '_')}`,
		// 		(data: any) => {
		// 			console.log('EMIT STATUS', data);
		// 			this.managerFacade.updateTable({...it, status: data ?? 'Failed'});
		// 		}
		// 	);
		// });
	}

	updateStatusOrderPool(data: {orderPool: OrderPool; status: STATUS_CHANGE}) {
		console.log(
			'HERE UPDATE POOL',
			data,
			this.orderService.getNextOrderStatus(data.orderPool.orderStatus)
		);
		if (!data || !data.orderPool || !data.status) {
			return;
		}
		switch (data.status) {
			case STATUS_CHANGE.PREV:
				this.managerFacade.updateOrderPool(
					data.orderPool,
					this.orderService.getPreviousOrderStatus(data.orderPool.orderStatus)
				);
				break;
			case STATUS_CHANGE.NEXT:
				this.managerFacade.updateOrderPool(
					data.orderPool,
					this.orderService.getNextOrderStatus(data.orderPool.orderStatus)
				);
				break;
			default:
				break;
		}
	}

	updateStatus(data: {order: Order; status: STATUS_CHANGE}) {
		if (!data || !data.order || !data.status) {
			return;
		}
		switch (data.status) {
			case STATUS_CHANGE.PREV:
				//TODO: fix when needed
				// this.managerFacade.updateOrderStatus(
				// 	data.order,
				// 	this.orderService.getPreviousOrderStatus(data.order.status)
				// );
				break;
			case STATUS_CHANGE.NEXT:
				this.managerFacade.updateOrderStatus(
					data.order,
					this.orderService.getNextOrderStatus(data.order.status)
				);
				break;
			default:
				break;
		}
	}

	// Resolves to true only if the sound was actually played
	async playClickSound(): Promise<boolean> {
		if (!AppComponent.musicOn || !AppComponent.audio) return false;

		try {
			AppComponent.audio.currentTime = 0;
			await AppComponent.audio.play();
			return true;
		} catch (e) {
			console.warn('Could not play order sound', e);
			return false;
		}
	}

	stockManager() {
		this.navService.stockManager();
	}

	openDialog(): void {
		const dialogRef = this.dialog.open(ArticleModalComponent, {
			data: {articleName: 'aperol'}
		});

		dialogRef.afterClosed().subscribe(result => {
			console.log(result);
		});
	}

	checkIfOrder(value: Order | OrderPool): value is Order {
		return (value as Order)?.orderedArticles != undefined;
	}

	checkIfOrderPool(value: Order | OrderPool): value is OrderPool {
		return (value as OrderPool)?.dndCompany != undefined;
	}

	private getStatusIndex = (item: Order | OrderPool): number => {
		if (this.checkIfOrder(item))
			return STATUS_ORDER.indexOf(item.status ?? null);
		if (this.checkIfOrderPool(item))
			return STATUS_ORDER.indexOf(item.orderStatus ?? null);
		return -1;
	};

	// Virtual orders are placed on the POS and sorted by creation time;
	// pools have no own time and go first within their status
	private getOrderTime = (item: Order | OrderPool): number => {
		if (!this.checkIfOrder(item)) return 0;
		const date =
			item.type === OrderType.VIRTUAL ? item.createdAt : item.orderAt;
		const time = new Date(date).getTime();
		return Number.isNaN(time) ? 0 : time;
	};

	private sortByStatus = (a: Order | OrderPool, b: Order | OrderPool) =>
		this.getStatusIndex(a) - this.getStatusIndex(b);
	private sortByTime = (a: Order | OrderPool, b: Order | OrderPool) =>
		this.getOrderTime(a) - this.getOrderTime(b);

	scrollLeft() {
		this.scrollableContent.nativeElement.scrollBy({
			left: -500,
			behavior: 'smooth'
		});
	}

	scrollRight() {
		this.scrollableContent.nativeElement.scrollBy({
			left: 500,
			behavior: 'smooth'
		});
	}

	/**
	 * Keeps only the orders that belong to one of the selected channels.
	 * An empty selection means "no filter" and every order is kept.
	 */
	filterOrderChannel(orders: Order[], channels: OrderChannelsFilter): Order[] {
		return filterOrderChannel(orders, channels);
	}

	mappedOrder(orderValue: Order) {
		return this.managerFacade.optionGroups$.pipe(
			map(groups =>
				this.mapOrderOptionGroups(
					structuredClone(orderValue),
					new Map(groups.map(it => [it._id, it]))
				)
			)
		);
	}

	// Adds the option group name and sort order to every selected option and
	// sorts the options by group. Returns a new order object, so OnPush cards
	// re-render when the option groups arrive after the orders.
	private mapOrderOptionGroups(
		order: Order,
		groupsMap: Map<string, OptionGroup>
	): Order {
		return {
			...order,
			orderedArticles: (order.orderedArticles ?? []).map(articleGroup => ({
				...articleGroup,
				groups: (articleGroup.groups ?? [])
					.map(group => {
						const optionGroup = groupsMap.get(group.group);
						if (!optionGroup) {
							return group;
						}
						return {
							...group,
							description: optionGroup.description?.de ?? '',
							sort: optionGroup.sortOrder ?? 0
						};
					})
					.sort((gr1, gr2) => (gr1.sort ?? 0) - (gr2.sort ?? 0))
			}))
		};
	}
}
